<template>
  <div class="dashboard">
    <!-- ===== Header ===== -->
    <div class="dash-header">
      <div class="dash-header-text">
        <h1 class="dash-title">Welcome back, {{ firstName }}</h1>
        <p class="dash-subtitle">
          Here's what's happening at
          <span class="font-weight-semibold">{{ company.name || 'your business' }}</span> today.
        </p>
      </div>
      <div class="dash-header-actions">
        <CButtonGroup size="sm" class="period-group">
          <CButton
            v-for="period in periodOptions"
            :key="period.value"
            :class="['period-btn', { active: selectedPeriod === period.value }]"
            @click="changePeriod(period.value)"
          >
            <span class="d-none d-md-inline">{{ period.label }}</span>
            <span class="d-md-none">{{ period.label.substr(0, 1) }}</span>
          </CButton>
        </CButtonGroup>
        <button
          type="button"
          class="refresh-btn"
          :disabled="loading"
          title="Refresh data"
          aria-label="Refresh"
          @click="refreshDashboardData"
        >
          <CIcon name="cil-reload" :class="{ 'spin': loading }" />
        </button>
      </div>
    </div>

    <!-- ===== KPI Stats ===== -->
    <CRow class="kpi-row" :class="{ loading: loading }">
      <CCol sm="6" xl="3" v-for="kpi in kpiCards" :key="kpi.key" class="mb-4">
        <router-link :to="kpi.url" class="kpi-card d-block text-reset text-decoration-none">
          <div class="kpi-top">
            <span class="kpi-label">{{ kpi.label }}</span>
            <span class="kpi-chip" :class="'chip-' + kpi.tone"><CIcon :name="kpi.icon" /></span>
          </div>
          <div class="kpi-value">{{ kpi.value }}</div>
          <div class="small text-muted mb-1">{{ kpi.period }}</div>
          <div class="kpi-foot">
            <span
              class="kpi-trend"
              :class="kpi.neutral ? 'trend-neutral' : (kpi.favorable ? 'trend-up' : 'trend-down')"
            >
              <CIcon v-if="!kpi.neutral" :name="kpi.changeUp ? 'cil-arrow-top' : 'cil-arrow-bottom'" />
              {{ kpi.changeText }}
            </span>
            <span class="kpi-sub">{{ kpi.sub }}</span>
          </div>
        </router-link>
      </CCol>
    </CRow>

    <!-- ===== Business Information (collapsible) ===== -->
    <CCard class="dash-card mb-4">
      <CCardHeader class="dash-card-header">
        <div class="d-flex justify-content-between align-items-center">
          <button
            type="button"
            class="btn btn-link p-0 d-flex align-items-center text-reset text-decoration-none"
            :aria-expanded="showBusinessInfo ? 'true' : 'false'"
            @click="toggleBusinessInfo"
          >
            <h5 class="dash-card-title mb-0">Business information</h5>
            <CIcon
              :name="showBusinessInfo ? 'cil-chevron-top' : 'cil-chevron-bottom'"
              class="ml-2 text-muted transition-icon"
            />
          </button>
          <CButton size="sm" class="btn-brand-outline" @click="editBusinessInfo">
            <CIcon name="cil-pencil" /> Edit details
          </CButton>
        </div>
      </CCardHeader>
      <CCollapse :show.sync="showBusinessInfo" :duration="300">
        <CCardBody class="p-4 pt-2">
          <CRow>
            <CCol lg="12" class="mb-4">
              <div class="d-flex align-items-center">
                <div class="mr-3">
                  <img
                    v-if="company.logoUrl"
                    :src="company.logoUrl"
                    alt="Business logo"
                    class="business-logo-large"
                  />
                  <div v-else class="business-logo-fallback">
                    {{ (company.name || 'N').charAt(0) }}
                  </div>
                </div>
                <div class="flex-grow-1">
                  <h3 class="mb-1 font-weight-bold">{{ company.name || '—' }}</h3>
                  <p class="text-muted mb-0 h6" v-if="company.tagLine">{{ company.tagLine }}</p>
                </div>
              </div>
            </CCol>

            <CCol lg="6" class="mb-4">
              <div class="info-section">
                <h6 class="section-title mb-3">
                  <CIcon name="cil-file" class="mr-2" /> Registration and tax
                </h6>
                <div class="info-item mb-3" v-if="company.regNo">
                  <label class="info-label">Registration no.</label>
                  <div class="info-value selectable-text">{{ company.regNo }}</div>
                </div>
                <div class="info-item mb-3" v-if="currentProfile.defaultBusiness && currentProfile.defaultBusiness.brnOld">
                  <label class="info-label">BRN (old format)</label>
                  <div class="info-value selectable-text">{{ currentProfile.defaultBusiness.brnOld }}</div>
                </div>
                <div class="info-item mb-3" v-if="currentProfile.defaultBusiness && currentProfile.defaultBusiness.brnNew">
                  <label class="info-label">BRN (new format)</label>
                  <div class="info-value selectable-text">{{ currentProfile.defaultBusiness.brnNew }}</div>
                </div>
                <div class="info-item mb-3" v-if="company.taxIdentificationNumber">
                  <label class="info-label">Tax identification number (TIN)</label>
                  <div class="info-value selectable-text">{{ company.taxIdentificationNumber }}</div>
                </div>
                <div class="info-item mb-3" v-if="currentProfile.defaultBusiness && currentProfile.defaultBusiness.sstNumber">
                  <label class="info-label">SST number</label>
                  <div class="info-value selectable-text">{{ currentProfile.defaultBusiness.sstNumber }}</div>
                </div>
                <div class="info-item mb-3" v-if="currentProfile.defaultBusiness && currentProfile.defaultBusiness.msicCode">
                  <label class="info-label">MSIC code</label>
                  <div class="info-value selectable-text">{{ currentProfile.defaultBusiness.msicCode }}</div>
                </div>
                <div class="info-item" v-if="currentProfile.defaultBusiness && currentProfile.defaultBusiness.dateIncorporation">
                  <label class="info-label">Date of incorporation</label>
                  <div class="info-value">{{ formatDate(currentProfile.defaultBusiness.dateIncorporation) }}</div>
                </div>
              </div>
            </CCol>

            <CCol lg="6" class="mb-4">
              <div class="info-section">
                <h6 class="section-title mb-3">
                  <CIcon name="cil-contact" class="mr-2" /> Contact
                </h6>
                <div class="info-item mb-3" v-if="company.address">
                  <label class="info-label">Address</label>
                  <div class="info-value selectable-text">{{ company.address }}</div>
                  <div class="info-value-small text-muted" v-if="company.postcode || company.city || company.state">
                    {{ company.postcode }} {{ company.city }}, {{ company.state }}, {{ company.country }}
                  </div>
                </div>
                <div class="info-item mb-3" v-if="company.phone">
                  <label class="info-label">Phone</label>
                  <div class="info-value selectable-text">
                    <CIcon name="cil-phone" class="mr-2 text-brand-accent" />{{ company.phone }}
                  </div>
                </div>
                <div class="info-item mb-3" v-if="company.email">
                  <label class="info-label">Email</label>
                  <div class="info-value selectable-text">
                    <CIcon name="cil-envelope-closed" class="mr-2 text-brand-accent" />{{ company.email }}
                  </div>
                </div>
                <div class="info-item" v-if="company.website">
                  <label class="info-label">Website</label>
                  <div class="info-value selectable-text">
                    <CIcon name="cil-globe-alt" class="mr-2 text-brand-accent" />
                    <a :href="company.website" target="_blank" class="text-brand-accent">{{ company.website }}</a>
                  </div>
                </div>
              </div>
            </CCol>
          </CRow>
        </CCardBody>
      </CCollapse>
    </CCard>

    <!-- ===== Cash Position + Bank Accounts ===== -->
    <CRow>
      <CCol lg="8">
        <CCard class="dash-card mb-4 h-100">
          <CCardHeader class="dash-card-header">
            <h5 class="dash-card-title mb-0">Cash position</h5>
            <small class="text-muted">Money in vs. out, last 6 months</small>
          </CCardHeader>
          <CCardBody class="pt-2 pb-4 px-4">
            <CRow class="mb-4">
              <CCol md="4">
                <div class="metric metric-info">
                  <div class="metric-label">Total cash</div>
                  <div class="metric-value">{{ formatCurrency(totalCash) }}</div>
                  <div class="metric-sub">{{ bankAccounts.length }} bank account{{ bankAccounts.length === 1 ? "" : "s" }}, current balance</div>
                </div>
              </CCol>
              <CCol md="4">
                <div class="metric metric-success">
                  <div class="metric-label">Receivables</div>
                  <div class="metric-value">{{ formatCurrency(receivables) }}</div>
                  <div class="metric-sub">Outstanding invoices, as of today</div>
                </div>
              </CCol>
              <CCol md="4">
                <div class="metric metric-danger">
                  <div class="metric-label">Payables</div>
                  <div class="metric-value">{{ formatCurrency(payables) }}</div>
                  <div class="metric-sub">Unpaid bills, as of today</div>
                </div>
              </CCol>
            </CRow>
            <div v-if="loading" class="chart-placeholder"><CSpinner color="primary" /></div>
            <CashPositionChart v-else :chartData="cashPositionChartData" style="height: 300px" />
          </CCardBody>
        </CCard>
      </CCol>

      <CCol lg="4">
        <BankAccountsWidget
          :accounts="getBankAccountsForWidget()"
          :loading="bankAccountsLoading"
          :currency="businessCurrency"
          @add-account="addBankAccount"
          @refresh-accounts="refreshBankAccounts"
          @view-all-accounts="navigateToBankList"
          @account-clicked="navigateToBankAccount"
          @view-statements="viewAccountStatements"
          @add-transaction="addAccountTransaction"
        />
      </CCol>
    </CRow>

    <!-- ===== Profit & Loss + Expenses ===== -->
    <CRow>
      <CCol lg="6">
        <CCard class="dash-card mb-4">
          <CCardHeader class="dash-card-header">
            <h5 class="dash-card-title mb-0">Profit and loss</h5>
            <small class="text-muted">{{ periodLabel }}</small>
          </CCardHeader>
          <CCardBody class="pt-2 pb-4 px-4">
            <CRow class="mb-4">
              <CCol md="4">
                <div class="metric metric-success">
                  <div class="metric-label">Revenue</div>
                  <div class="metric-value">{{ formatCurrency(revenue) }}</div>
                  <div class="metric-sub" :class="trendClass(revenueChange)">
                    <CIcon :name="revenueChange >= 0 ? 'cil-arrow-top' : 'cil-arrow-bottom'" />
                    {{ formatPercent(revenueChange) }} vs previous {{ periodUnit }}
                  </div>
                </div>
              </CCol>
              <CCol md="4">
                <div class="metric metric-danger">
                  <div class="metric-label">Expenses</div>
                  <div class="metric-value">{{ formatCurrency(expensesTotal) }}</div>
                  <div class="metric-sub" :class="trendClass(-expensesChange)">
                    <CIcon :name="expensesChange <= 0 ? 'cil-arrow-bottom' : 'cil-arrow-top'" />
                    {{ formatPercent(expensesChange) }} vs previous {{ periodUnit }}
                  </div>
                </div>
              </CCol>
              <CCol md="4">
                <div class="metric metric-navy">
                  <div class="metric-label">Net profit</div>
                  <div class="metric-value">{{ formatCurrency(netProfit) }}</div>
                  <div class="metric-sub" :class="trendClass(profitChange)">
                    <CIcon :name="profitChange >= 0 ? 'cil-arrow-top' : 'cil-arrow-bottom'" />
                    {{ formatPercent(profitChange) }} vs previous {{ periodUnit }}
                  </div>
                </div>
              </CCol>
            </CRow>
            <div v-if="loading" class="chart-placeholder"><CSpinner color="primary" /></div>
            <ProfitLossChart v-else :chartData="profitLossChartData" style="height: 300px" />
          </CCardBody>
        </CCard>
      </CCol>

      <CCol lg="6">
        <CCard class="dash-card mb-4">
          <CCardHeader class="dash-card-header">
            <h5 class="dash-card-title mb-0">Expenses by category</h5>
            <small class="text-muted">{{ periodLabel }}</small>
          </CCardHeader>
          <CCardBody class="pt-2 pb-4 px-4">
            <div v-if="loading" class="chart-placeholder"><CSpinner color="primary" /></div>
            <div v-else-if="expenseCategories.length === 0" class="empty-state">
              <CIcon name="cil-chart-pie" size="2xl" class="text-muted mb-2" />
              <div>No expenses recorded for this period.</div>
            </div>
            <CRow v-else>
              <CCol md="5" class="d-flex justify-content-center align-items-center">
                <div style="height: 200px; width: 200px">
                  <ExpensesChart :chartData="expensesChartData" />
                </div>
              </CCol>
              <CCol md="7">
                <div class="table-responsive">
                  <table class="table table-borderless dash-table mb-0">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th class="text-right">Amount</th>
                        <th class="text-right">%</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(cat, index) in expenseCategories" :key="cat.category">
                        <td>
                          <span class="cat-dot" :style="{ backgroundColor: getCategoryColor(index) }"></span>
                          {{ cat.category }}
                        </td>
                        <td class="text-right">{{ formatCurrency(cat.amount) }}</td>
                        <td class="text-right">{{ cat.percentage }}%</td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr class="font-weight-bold">
                        <td>Total</td>
                        <td class="text-right">{{ formatCurrency(expensesPeriodTotal) }}</td>
                        <td class="text-right">100%</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- ===== Recent Invoices + Upcoming Deadlines ===== -->
    <CRow>
      <CCol lg="6">
        <CCard class="dash-card mb-4">
          <CCardHeader class="dash-card-header">
            <h5 class="dash-card-title mb-0">Recent invoices</h5>
            <CButton size="sm" class="btn-brand-accent" @click="navigateTo('/tenants/Invoice')">
              <CIcon name="cil-plus" /> Create invoice
            </CButton>
          </CCardHeader>
          <CCardBody class="pt-2 pb-4 px-4">
            <div v-if="loading" class="chart-placeholder"><CSpinner color="primary" /></div>
            <div v-else-if="recentInvoices.length === 0" class="empty-state">
              <CIcon name="cil-file" size="2xl" class="text-muted mb-2" />
              <div>No invoices yet.</div>
            </div>
            <div v-else class="table-responsive">
              <table class="table table-hover dash-table mb-0">
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>Customer</th>
                    <th class="text-right">Amount</th>
                    <th>Status</th>
                    <th>Due</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="inv in recentInvoices" :key="inv.id">
                    <td class="font-weight-bold">
                      <router-link :to="`/tenants/Invoice/${inv.id}`" class="text-monospace">{{ inv.invoiceNumber }}</router-link>
                    </td>
                    <td>{{ inv.customerName }}</td>
                    <td class="text-right font-weight-semibold">{{ formatCurrency(inv.amount) }}</td>
                    <td><CBadge :color="invoiceStatusColor(inv.displayStatus)">{{ inv.displayStatus }}</CBadge></td>
                    <td><span :class="getDueDateClass(inv.dueDate)">{{ inv.dueDate ? formatDate(inv.dueDate) : '—' }}</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="d-flex justify-content-end mt-3" v-if="recentInvoices.length">
              <CButton color="link" size="sm" class="p-0 text-brand-accent" @click="navigateTo('/tenants/InvoiceList')">View all invoices</CButton>
            </div>
          </CCardBody>
        </CCard>
      </CCol>

      <CCol lg="6">
        <CCard class="dash-card mb-4">
          <CCardHeader class="dash-card-header">
            <h5 class="dash-card-title mb-0">Upcoming deadlines</h5>
            <small class="text-muted">Receivables, payables &amp; payroll</small>
          </CCardHeader>
          <CCardBody class="pt-2 pb-4 px-4">
            <div v-if="loading" class="chart-placeholder"><CSpinner color="primary" /></div>
            <div v-else-if="upcomingDeadlines.length === 0" class="empty-state">
              <CIcon name="cil-calendar-check" size="2xl" class="text-muted mb-2" />
              <div>Nothing due soon. You're all caught up.</div>
            </div>
            <div v-else>
              <div
                v-for="deadline in upcomingDeadlines"
                :key="deadline.id"
                class="deadline-item"
                :class="getDeadlineClass(deadline.date)"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <div class="font-weight-semibold">{{ deadline.title }}</div>
                    <div class="small text-muted d-flex align-items-center mt-1">
                      <CIcon name="cil-calendar" class="mr-1" />
                      <span>{{ formatDate(deadline.date) }}</span>
                      <span class="mx-2">•</span>
                      <span>{{ deadline.type }}</span>
                      <span v-if="deadline.amount" class="mx-2">•</span>
                      <span v-if="deadline.amount" class="font-weight-semibold">{{ formatCurrency(deadline.amount) }}</span>
                    </div>
                  </div>
                  <CBadge :color="getDeadlineColor(deadline.date)">{{ getDeadlineDaysText(deadline.date) }}</CBadge>
                </div>
              </div>
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- ===== Recent Activity + Quick Actions ===== -->
    <CRow>
      <CCol lg="8">
        <CCard class="dash-card mb-4">
          <CCardHeader class="dash-card-header">
            <h5 class="dash-card-title mb-0">Recent activity</h5>
          </CCardHeader>
          <CCardBody class="pt-2 pb-4 px-4">
            <div v-if="loading" class="chart-placeholder"><CSpinner color="primary" /></div>
            <div v-else-if="recentActivities.length === 0" class="empty-state">
              <CIcon name="cil-history" size="2xl" class="text-muted mb-2" />
              <div>No recent activity.</div>
            </div>
            <div v-else class="timeline">
              <div v-for="activity in recentActivities" :key="activity.id" class="timeline-item">
                <div class="timeline-left">
                  <div class="timeline-avatar" :class="`avatar-${activity.color}`">
                    <CIcon :name="activity.icon" />
                  </div>
                </div>
                <div class="timeline-content">
                  <div class="d-flex justify-content-between">
                    <div class="font-weight-semibold">{{ activity.title }}</div>
                    <div class="small text-muted">{{ formatTimeAgo(activity.date) }}</div>
                  </div>
                  <div class="small text-muted">{{ activity.description }}</div>
                </div>
              </div>
            </div>
          </CCardBody>
        </CCard>
      </CCol>

      <CCol lg="4">
        <CCard class="dash-card mb-4">
          <CCardHeader class="dash-card-header">
            <h5 class="dash-card-title mb-0">Quick actions</h5>
          </CCardHeader>
          <CCardBody class="pt-2 pb-4 px-4">
            <div class="quick-grid">
              <router-link
                v-for="action in quickActions"
                :key="action.id"
                :to="action.url"
                class="quick-action text-reset text-decoration-none"
              >
                <div class="quick-icon" :class="`qi-${action.color}`">
                  <CIcon :name="action.icon" />
                </div>
                <div class="quick-label">{{ action.title }}</div>
              </router-link>
            </div>
          </CCardBody>
        </CCard>

        <CCard class="dash-card mb-4">
          <CCardHeader class="dash-card-header">
            <h5 class="dash-card-title mb-0">Shortcuts</h5>
          </CCardHeader>
          <CCardBody class="pt-2 pb-4 px-4">
            <div class="shortcut-grid">
              <router-link
                v-for="shortcut in shortcuts"
                :key="shortcut.id"
                :to="shortcut.url"
                class="shortcut text-reset text-decoration-none"
              >
                <CIcon :name="shortcut.icon" class="shortcut-icon" />
                <div class="shortcut-label">{{ shortcut.title }}</div>
              </router-link>
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import { freeSet } from "@coreui/icons";
import {
  mytToday,
  asDateString,
  addDays,
  diffDays,
  formatDate as formatDisplayDate,
} from "../utils/datetime";
import { formatMyr } from "../utils/money";

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// "yyyy-MM-dd" of the first day of the month `offset` months before (y, m), m being 1-based.
function monthStart(y, m, offset) {
  const idx = y * 12 + (m - 1) - (offset || 0);
  const yy = Math.floor(idx / 12);
  const mm = (idx % 12) + 1;
  return `${yy}-${String(mm).padStart(2, "0")}-01`;
}
function monthEnd(start) {
  const [y, m] = start.split("-").map(Number);
  return addDays(monthStart(y, m, -1), -1);
}
import ProfitLossChart from "./widgets/ProfitLossChart.vue";
import ExpensesChart from "./widgets/ExpensesChart.vue";
import CashPositionChart from "./widgets/CashPositionChart.vue";
import BankAccountsWidget from "./widgets/BankAccountsWidget.vue";
import ProfileApi from "../lib/profileApi";
import BankApi from "../lib/bankApi";
import InvoiceApi from "../lib/invoiceApi";
import BillApi from "../lib/billApi";
import ExpenseApi from "../lib/expenseApi";
import PayrollApi from "../lib/payrollApi";

// Brand palette (mirrors marketing.css tokens) for chart datasets.
const BRAND = {
  navy: "#1E3A5F",
  accent: "#10B981",
  danger: "#EF4444",
  warning: "#F59E0B",
  info: "#3B82F6",
  gray: "#94A3B8",
};

export default {
  name: "Dashboard",
  freeSet,
  components: {
    ProfitLossChart,
    ExpensesChart,
    CashPositionChart,
    BankAccountsWidget,
  },
  data() {
    return {
      // API clients
      profileApi: new ProfileApi(),
      bankApi: new BankApi(),
      invoiceApi: new InvoiceApi(),
      billApi: new BillApi(),
      expenseApi: new ExpenseApi(),
      payrollApi: new PayrollApi(),

      currentProfile: {},
      currentUser: { name: "", role: "" },
      company: {},

      showBusinessInfo: true,

      // Raw data (real, fetched)
      invoicesRaw: [],
      billsRaw: [],
      expensesRaw: [],
      bankAccounts: [],
      payrollsRaw: [],

      // Loading flags
      loading: false,
      bankAccountsLoading: false,

      selectedPeriod: "monthly",
      periodOptions: [
        { value: "daily", label: "Daily" },
        { value: "weekly", label: "Weekly" },
        { value: "monthly", label: "Monthly" },
        { value: "yearly", label: "Yearly" },
      ],

      quickActions: [
        { id: 1, title: "New invoice", icon: "cil-file", color: "primary", url: "/tenants/Invoice" },
        { id: 2, title: "New bill", icon: "cil-credit-card", color: "danger", url: "/tenants/bill" },
        { id: 3, title: "New expense", icon: "cil-wallet", color: "warning", url: "/tenants/OtherExpense" },
        { id: 4, title: "New quotation", icon: "cil-clipboard", color: "info", url: "/tenants/quotation" },
        { id: 5, title: "New customer", icon: "cil-user-plus", color: "success", url: "/tenants/Customer" },
        { id: 6, title: "Run payroll", icon: "cil-people", color: "navy", url: "/tenants/payroll" },
      ],
      shortcuts: [
        { id: 1, title: "Invoices", icon: "cil-file", url: "/tenants/InvoiceList" },
        { id: 2, title: "Bills", icon: "cil-credit-card", url: "/tenants/BillList" },
        { id: 3, title: "Expenses", icon: "cil-wallet", url: "/tenants/OtherExpenseList" },
        { id: 4, title: "Quotations", icon: "cil-clipboard", url: "/tenants/quotationList" },
        { id: 5, title: "Customers", icon: "cil-people", url: "/tenants/CustomerList" },
        { id: 6, title: "Payroll", icon: "cil-money", url: "/tenants/payrollList" },
        { id: 7, title: "Banking", icon: "cil-bank", url: "/tenants/BankList" },
        { id: 8, title: "Cash flow", icon: "cil-chart-line", url: "/tenants/dashboardCashFlow" },
      ],
    };
  },

  watch: {
    currentProfile: {
      handler(newProfile) {
        if (newProfile && newProfile.defaultBusiness) {
          this.populateCompanyData(newProfile);
          this.populateUserData(newProfile);
        }
      },
      deep: true,
      immediate: true,
    },
  },

  mounted() {
    this.$store.commit("setPageNavItems", ["Dashboard", "Home"]);
    this.fetchAll();
    this.refreshInterval = setInterval(() => {
      if (!this.loading) this.fetchAll();
    }, 5 * 60 * 1000);
  },

  beforeDestroy() {
    if (this.refreshInterval) clearInterval(this.refreshInterval);
  },

  computed: {
    firstName() {
      const name = this.currentUser.name || "there";
      return name.split(" ")[0];
    },

    businessCurrency() {
      const fromBusiness =
        (typeof auth !== "undefined" && auth.getLocalBusiness && auth.getLocalBusiness()) || {};
      return this.company.currency || fromBusiness.currency || "MYR";
    },

    periodLabel() {
      const map = { daily: "Today", weekly: "This week", monthly: "This month", yearly: "This year" };
      return map[this.selectedPeriod] || "This month";
    },
    periodUnit() {
      return { daily: "day", weekly: "week", monthly: "month", yearly: "year" }[this.selectedPeriod] || "month";
    },

    // ---- Period ranges ----
    periodRange() {
      return this.periodBounds(this.selectedPeriod, 0);
    },
    prevPeriodRange() {
      return this.periodBounds(this.selectedPeriod, 1);
    },

    // ---- Revenue (approved invoices issued in period) ----
    revenue() {
      return this.sumInvoices(this.periodRange);
    },
    prevRevenue() {
      return this.sumInvoices(this.prevPeriodRange);
    },
    revenueChange() {
      return this.pctChange(this.revenue, this.prevRevenue);
    },

    // ---- Expenses (P&L = approved/paid expenses + approved bills in period) ----
    expensesTotal() {
      return this.sumExpensesAndBills(this.periodRange);
    },
    prevExpensesTotal() {
      return this.sumExpensesAndBills(this.prevPeriodRange);
    },
    expensesChange() {
      return this.pctChange(this.expensesTotal, this.prevExpensesTotal);
    },

    netProfit() {
      return this.revenue - this.expensesTotal;
    },
    prevNetProfit() {
      return this.prevRevenue - this.prevExpensesTotal;
    },
    profitChange() {
      return this.pctChange(this.netProfit, this.prevNetProfit);
    },

    // ---- Snapshots ----
    receivables() {
      return this.invoicesRaw
        .filter((i) => this.isApproved(i.statusDescription))
        .reduce((s, i) => s + this.invoiceOutstanding(i), 0);
    },
    payables() {
      return this.billsRaw
        .filter((b) => this.isApproved(b.stateDescription))
        .reduce((s, b) => s + (Number(b.amountDue) || 0), 0);
    },
    totalCash() {
      return this.bankAccounts.reduce((s, a) => s + (Number(a.balance) || 0), 0);
    },

    // ---- Pending invoices (approved, not fully paid) ----
    pendingInvoiceList() {
      return this.invoicesRaw.filter(
        (i) => this.isApproved(i.statusDescription) && !this.isInvoicePaid(i)
      );
    },
    pendingInvoicesAmount() {
      return this.pendingInvoiceList.reduce((s, i) => s + this.invoiceOutstanding(i), 0);
    },
    pendingInvoicesCount() {
      return this.pendingInvoiceList.length;
    },
    invoicesIssuedChange() {
      const cur = this.invoicesRaw.filter(
        (i) => this.isApproved(i.statusDescription) && this.inRange(i.issuedDate, this.periodRange)
      ).length;
      const prev = this.invoicesRaw.filter(
        (i) => this.isApproved(i.statusDescription) && this.inRange(i.issuedDate, this.prevPeriodRange)
      ).length;
      return this.pctChange(cur, prev);
    },

    kpiCards() {
      const vsPrev = "vs last " + this.periodUnit;
      const period = this.periodLabel;
      const pct = (n) => `${this.round1(Math.abs(n))}%`;
      const count = this.pendingInvoicesCount;
      return [
        {
          key: "revenue", label: "Revenue", icon: "cil-money", tone: "navy",
          value: this.formatCurrency(this.revenue), period,
          changeText: pct(this.revenueChange), changeUp: this.revenueChange >= 0,
          favorable: this.revenueChange >= 0, sub: vsPrev, url: "/tenants/InvoiceList",
        },
        {
          key: "expenses", label: "Expenses", icon: "cil-cart", tone: "danger",
          value: this.formatCurrency(this.expensesTotal), period,
          changeText: pct(this.expensesChange), changeUp: this.expensesChange >= 0,
          favorable: this.expensesChange <= 0, sub: vsPrev, url: "/tenants/OtherExpenseList",
        },
        {
          key: "profit", label: "Net profit", icon: "cil-chart-line", tone: "success",
          value: this.formatCurrency(this.netProfit), period,
          changeText: pct(this.profitChange), changeUp: this.profitChange >= 0,
          favorable: this.profitChange >= 0, sub: vsPrev, url: "/tenants/dashboardCashFlow",
        },
        {
          key: "pending", label: "Unpaid invoices", icon: "cil-file", tone: "warning",
          value: this.formatCurrency(this.pendingInvoicesAmount), period: "All open invoices, as of today",
          neutral: true, changeText: `${count} invoice${count === 1 ? "" : "s"}`,
          sub: "awaiting payment", url: "/tenants/InvoiceList",
        },
      ];
    },

    // ---- Expenses by category (period) ----
    expensesPeriodTotal() {
      return this.expensesRaw
        .filter((e) => this.isExpenseCounted(e) && this.inRange(e.date, this.periodRange))
        .reduce((s, e) => s + (Number(e.totalAmount) || 0), 0);
    },
    expenseCategories() {
      const map = {};
      this.expensesRaw
        .filter((e) => this.isExpenseCounted(e) && this.inRange(e.date, this.periodRange))
        .forEach((e) => {
          const cat = e.expenseType || (e.vendor && e.vendor.name) || e.name || "Other";
          map[cat] = (map[cat] || 0) + (Number(e.totalAmount) || 0);
        });
      let cats = Object.keys(map).map((k) => ({ category: k, amount: map[k] }));
      cats.sort((a, b) => b.amount - a.amount);
      // Top 5 + Others
      if (cats.length > 6) {
        const top = cats.slice(0, 5);
        const othersAmount = cats.slice(5).reduce((s, c) => s + c.amount, 0);
        top.push({ category: "Others", amount: othersAmount });
        cats = top;
      }
      const total = this.expensesPeriodTotal || 1;
      return cats.map((c) => ({ ...c, percentage: Math.round((c.amount / total) * 1000) / 10 }));
    },
    expensesChartData() {
      const palette = [BRAND.navy, BRAND.accent, BRAND.danger, BRAND.warning, BRAND.info, BRAND.gray];
      return {
        labels: this.expenseCategories.map((c) => c.category),
        datasets: [
          {
            data: this.expenseCategories.map((c) => c.amount),
            backgroundColor: this.expenseCategories.map((c, i) => palette[i % palette.length]),
          },
        ],
      };
    },

    // ---- Monthly series (last 6 months) ----
    last6Months() {
      const [y, m] = mytToday().split("-").map(Number);
      const arr = [];
      for (let i = 5; i >= 0; i--) {
        const start = monthStart(y, m, i);
        arr.push({
          label: MONTH_LABELS[Number(start.substring(5, 7)) - 1],
          start,
          end: monthEnd(start),
        });
      }
      return arr;
    },
    profitLossChartData() {
      const months = this.last6Months;
      const revenue = months.map((m) => this.sumInvoices(m));
      const expenses = months.map((m) => this.sumExpensesAndBills(m));
      return {
        labels: months.map((m) => m.label),
        datasets: [
          this.lineDataset("Revenue", revenue, BRAND.accent),
          this.lineDataset("Expenses", expenses, BRAND.danger),
          this.lineDataset("Net profit", revenue.map((r, i) => r - expenses[i]), BRAND.navy),
        ],
      };
    },
    cashPositionChartData() {
      const months = this.last6Months;
      const moneyIn = months.map((m) => this.sumMoneyIn(m));
      const moneyOut = months.map((m) => this.sumMoneyOut(m));
      return {
        labels: months.map((m) => m.label),
        datasets: [
          this.lineDataset("Money in", moneyIn, BRAND.accent),
          this.lineDataset("Money out", moneyOut, BRAND.danger),
          this.lineDataset("Net", moneyIn.map((v, i) => v - moneyOut[i]), BRAND.info),
        ],
      };
    },

    // ---- Recent invoices (top 5 by issued date) ----
    recentInvoices() {
      return [...this.invoicesRaw]
        .sort((a, b) => new Date(b.issuedDate) - new Date(a.issuedDate))
        .slice(0, 5)
        .map((i) => ({
          id: i.id,
          invoiceNumber: i.invoiceNumber || "—",
          customerName: (i.customer && i.customer.name) || "—",
          amount: this.invoiceAmount(i),
          dueDate: i.dueDate,
          displayStatus: this.invoiceDisplayStatus(i),
        }));
    },

    // ---- Recent activity (merged real records) ----
    recentActivities() {
      const acts = [];
      this.invoicesRaw.forEach((i) => {
        acts.push({
          id: "inv-" + i.id,
          date: i.issuedDate || i.createdOn,
          title: "Invoice " + (i.invoiceNumber || ""),
          description: ((i.customer && i.customer.name) || "—") + " · " + this.formatCurrency(this.invoiceAmount(i)),
          icon: "cil-file",
          color: "primary",
        });
        (i.payments || []).forEach((p) => {
          acts.push({
            id: "pay-" + p.id,
            date: p.paymentDate,
            title: "Payment received",
            description: ((i.customer && i.customer.name) || "—") + " · " + this.formatCurrency(p.amount),
            icon: "cil-check-circle",
            color: "success",
          });
        });
      });
      this.billsRaw.forEach((b) => {
        acts.push({
          id: "bill-" + b.id,
          date: b.date,
          title: "Bill " + (b.billNo || ""),
          description: ((b.vendor && b.vendor.name) || "—") + " · " + this.formatCurrency(b.totalAmount),
          icon: "cil-credit-card",
          color: "warning",
        });
      });
      this.expensesRaw.forEach((e) => {
        acts.push({
          id: "exp-" + e.id,
          date: e.date,
          title: "Expense recorded",
          description:
            (e.expenseType || (e.vendor && e.vendor.name) || e.name || "Expense") +
            " · " + this.formatCurrency(e.totalAmount),
          icon: "cil-wallet",
          color: "danger",
        });
      });
      return acts
        .filter((a) => this.isRealDate(a.date))
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 7);
    },

    // ---- Upcoming deadlines (real due dates) ----
    upcomingDeadlines() {
      const items = [];
      const horizon = addDays(mytToday(), -30); // include recently overdue
      this.invoicesRaw
        .filter((i) => this.isApproved(i.statusDescription) && !this.isInvoicePaid(i) && i.dueDate)
        .forEach((i) =>
          items.push({
            id: "inv-" + i.id,
            title: "Invoice " + (i.invoiceNumber || "") + " due",
            date: i.dueDate,
            type: "Receivable",
            amount: this.invoiceOutstanding(i),
          })
        );
      this.billsRaw
        .filter((b) => (Number(b.amountDue) || 0) > 0 && this.isRealDate(b.dueDate))
        .forEach((b) =>
          items.push({
            id: "bill-" + b.id,
            title: "Bill " + (b.billNo || "") + " due",
            date: b.dueDate,
            type: "Payable",
            amount: Number(b.amountDue) || 0,
          })
        );
      this.payrollsRaw
        .filter((p) => p.statusDescription !== "Paid" && p.date)
        .forEach((p) =>
          items.push({
            id: "pay-" + p.id,
            title: "Payroll " + (p.payrollNo || ""),
            date: p.date,
            type: "Payroll",
            amount: Number(p.netAmount) || 0,
          })
        );
      return items
        .filter((d) => this.isRealDate(d.date) && asDateString(d.date) > horizon)
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .slice(0, 6);
    },
  },

  methods: {
    // ===== Fetching =====
    fetchAll() {
      this.fetchCurrentProfile();
      this.fetchFinancials();
      this.fetchBankAccounts();
    },

    async fetchFinancials() {
      this.loading = true;
      const unwrap = (res) => (res && (res.result?.data ?? res.result)) || [];
      const results = await Promise.allSettled([
        this.invoiceApi.getListByCurrentBusiness({ page: 1, pageSize: 1000, sortBy: "issueddate", sortDirection: "desc" }),
        this.billApi.getListByCurrentBusiness({ page: 1, pageSize: 1000 }),
        this.expenseApi.getListByCurrentBusiness({ page: 1, pageSize: 1000 }),
        this.payrollApi.getListByCurrentBusiness(),
      ]);
      const [inv, bill, exp, pay] = results;
      if (inv.status === "fulfilled") this.invoicesRaw = unwrap(inv.value);
      if (bill.status === "fulfilled") this.billsRaw = unwrap(bill.value);
      if (exp.status === "fulfilled") this.expensesRaw = unwrap(exp.value);
      if (pay.status === "fulfilled") this.payrollsRaw = unwrap(pay.value);
      const failed = results.filter((r) => r.status === "rejected");
      if (failed.length) {
        failed.forEach((r) => console.error("Dashboard load failed", r.reason));
        this.$toast.warning("Some figures couldn't be loaded, so totals may be incomplete. Refresh to try again.");
      }
      this.loading = false;
    },

    async fetchBankAccounts() {
      try {
        this.bankAccountsLoading = true;
        const response = await this.bankApi.getListByCurrentBusiness();
        const rows = (response && (response.result?.data ?? response.result)) || [];
        this.bankAccounts = rows.map((account) => ({
          id: account.id,
          bank: account.name,
          accountNumber: account.accountNo,
          accountName: account.accountName,
          balance: Number(account.balance) || 0,
          currency: account.currency || this.businessCurrency,
          change: 0, // no historical balance source
          minBalance: Number(account.minBalance) || 0,
          lastUpdated: account.updatedOn || account.createdOn,
          status: account.status || "Active",
          accountType: account.accountType || "Business Account",
          statementsCount: (account.bankStatements && account.bankStatements.length) || 0,
        }));
      } catch (error) {
        console.error("Bank accounts load failed", error);
        this.$toast.error("Couldn't load your bank accounts. Refresh to try again.");
      } finally {
        this.bankAccountsLoading = false;
      }
    },

    fetchCurrentProfile() {
      this.profileApi
        .getCurrentProfile()
        .then((response) => {
          this.currentProfile = response.result;
        })
        .catch((error) => {
          console.error("Profile load failed", error);
          this.$toast.error("Couldn't load your business details. Refresh to try again.");
        });
    },

    // ===== Aggregation helpers =====
    periodBounds(period, offset) {
      // Ranges are inclusive "yyyy-MM-dd" strings in Malaysia time; weeks start on Monday.
      const n = offset || 0;
      const today = mytToday();
      const [y, m] = today.split("-").map(Number);
      if (period === "daily") {
        const d = addDays(today, -n);
        return { start: d, end: d };
      }
      if (period === "weekly") {
        const [yy, mm, dd] = today.split("-").map(Number);
        const dow = (new Date(Date.UTC(yy, mm - 1, dd)).getUTCDay() + 6) % 7; // Mon = 0
        const start = addDays(today, -dow - 7 * n);
        return { start, end: addDays(start, 6) };
      }
      if (period === "yearly") {
        return { start: `${y - n}-01-01`, end: `${y - n}-12-31` };
      }
      const start = monthStart(y, m, n);
      return { start, end: monthEnd(start) };
    },
    inRange(date, range) {
      const d = asDateString(date);
      return !!d && d >= range.start && d <= range.end;
    },
    // API returns 0001-01-01 for "no date".
    isRealDate(date) {
      const d = asDateString(date);
      return !!d && d.substring(0, 4) > "0001";
    },
    isApproved(statusDescription) {
      return statusDescription === "Approve";
    },
    isExpenseCounted(e) {
      return e.expenseStateDescription === "Approve" || e.expenseStateDescription === "Paid";
    },

    invoiceAmount(inv) {
      const g = Number(inv.grandTotal) || 0;
      const t = Number(inv.totalAmount) || 0;
      if (g > 0) return g;
      if (t > 0) return t;
      if (Array.isArray(inv.items)) {
        return inv.items.reduce((s, it) => s + (Number(it.price) || 0) * (Number(it.quantity) || 0), 0);
      }
      return 0;
    },
    invoicePaid(inv) {
      return (inv.payments || []).reduce((s, p) => s + (Number(p.amount) || 0), 0);
    },
    invoiceOutstanding(inv) {
      return Math.max(0, this.invoiceAmount(inv) - this.invoicePaid(inv));
    },
    isInvoicePaid(inv) {
      return inv.isPaid === true || this.invoiceOutstanding(inv) <= 0.001;
    },
    isInvoiceOverdue(inv) {
      return !this.isInvoicePaid(inv) && this.isRealDate(inv.dueDate) && asDateString(inv.dueDate) < mytToday();
    },
    invoiceDisplayStatus(inv) {
      if (inv.statusDescription === "Draft") return "Draft";
      if (this.isInvoicePaid(inv)) return "Paid";
      if (this.isInvoiceOverdue(inv)) return "Overdue";
      return "Outstanding";
    },

    sumInvoices(range) {
      return this.invoicesRaw
        .filter((i) => this.isApproved(i.statusDescription) && this.inRange(i.issuedDate, range))
        .reduce((s, i) => s + this.invoiceAmount(i), 0);
    },
    sumExpensesAndBills(range) {
      const exp = this.expensesRaw
        .filter((e) => this.isExpenseCounted(e) && this.inRange(e.date, range))
        .reduce((s, e) => s + (Number(e.totalAmount) || 0), 0);
      const bills = this.billsRaw
        .filter((b) => this.isApproved(b.stateDescription) && this.inRange(b.date, range))
        .reduce((s, b) => s + (Number(b.totalAmount) || 0), 0);
      return exp + bills;
    },
    sumMoneyIn(range) {
      // Invoice payments received within the month
      let total = 0;
      this.invoicesRaw.forEach((i) => {
        (i.payments || []).forEach((p) => {
          if (this.inRange(p.paymentDate, range)) total += Number(p.amount) || 0;
        });
      });
      return total;
    },
    sumMoneyOut(range) {
      // Expenses + bill payments paid within the month
      let total = 0;
      this.expensesRaw.forEach((e) => {
        if (e.isPaid && this.inRange(e.paymentDate, range)) total += Number(e.totalAmount) || 0;
      });
      this.billsRaw.forEach((b) => {
        (b.payments || []).forEach((p) => {
          if (this.inRange(p.paymentDate, range)) total += Number(p.amount) || 0;
        });
      });
      return total;
    },

    pctChange(cur, prev) {
      if (!prev) return cur > 0 ? 100 : 0;
      return ((cur - prev) / Math.abs(prev)) * 100;
    },
    round1(n) {
      return Math.round((Number(n) || 0) * 10) / 10;
    },
    lineDataset(label, data, color) {
      return {
        label,
        backgroundColor: this.hexToRgba(color, 0.15),
        borderColor: color,
        pointBackgroundColor: color,
        pointBorderColor: "#fff",
        data,
      };
    },
    hexToRgba(hex, alpha) {
      const h = hex.replace("#", "");
      const r = parseInt(h.substring(0, 2), 16);
      const g = parseInt(h.substring(2, 4), 16);
      const b = parseInt(h.substring(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },

    // ===== Period / refresh =====
    changePeriod(period) {
      this.selectedPeriod = period;
    },
    refreshDashboardData() {
      this.fetchAll();
    },

    // ===== Bank widget =====
    getBankAccountsForWidget() {
      return this.bankAccounts
        .filter((a) => a.status !== "Inactive" && a.status !== "Closed")
        .sort((a, b) => (b.balance || 0) - (a.balance || 0))
        .slice(0, 3)
        .map((account) => ({
          ...account,
          displayBalance: this.formatCurrency(account.balance || 0, account.currency),
          healthPercentage: this.getAccountHealthPercentage(account),
          healthColor: this.getAccountHealthColor(account),
          healthLabel: this.getAccountHealthLabel(account),
          bankLogo: "",
          formattedAccountNumber: this.formatAccountNumber(account.accountNumber),
        }));
    },
    getAccountHealthPercentage(account) {
      if (!account.minBalance || account.minBalance <= 0) return 100;
      return Math.max(0, Math.min(100, (account.balance / (account.minBalance * 3)) * 100));
    },
    getAccountHealthColor(account) {
      const h = this.getAccountHealthPercentage(account);
      if (h >= 75) return "success";
      if (h >= 40) return "warning";
      return "danger";
    },
    getAccountHealthLabel(account) {
      const h = this.getAccountHealthPercentage(account);
      if (h >= 75) return "Good";
      if (h >= 40) return "Moderate";
      return "Low";
    },
    formatAccountNumber(accountNumber) {
      if (!accountNumber) return "N/A";
      const digits = String(accountNumber).replace(/\s|-/g, "");
      if (digits.length <= 4) return accountNumber;
      return `•••• ${digits.slice(-4)}`;
    },
    refreshBankAccounts() {
      this.fetchBankAccounts();
    },
    addBankAccount() {
      this.$router.push("/tenants/Bank");
    },
    navigateToBankAccount(account) {
      this.$router.push(`/tenants/Bank/${account.id}`);
    },
    navigateToBankList() {
      this.$router.push("/tenants/BankList");
    },
    viewAccountStatements(account) {
      this.$router.push(`/tenants/Bank/${account.id}#statements`);
    },
    addAccountTransaction() {
      this.$router.push("/tenants/Transaction");
    },

    // ===== Profile population =====
    populateCompanyData(profile) {
      if (!profile.defaultBusiness) return;
      const b = profile.defaultBusiness;
      this.company = {
        name: b.name || "",
        address: b.address || "",
        email: profile.email || "",
        phone: b.phone || "",
        regNo: b.regNo || "",
        currency: b.currency || "",
        website: b.website || "",
        tagLine: b.tagLine || "",
        logoUrl: this.getImageUrl(b.logoUrl),
        city: b.city || "",
        country: b.country || "",
        state: b.state || "",
        postcode: b.postcode || "",
        taxIdentificationNumber: b.taxIdentificationNumber || "",
        shortName: b.shortName || "",
        brnOld: b.brnOld || "",
        brnNew: b.brnNew || "",
        sstNumber: b.sstNumber || "",
        msicCode: b.msicCode || "",
        dateIncorporation: b.dateIncorporation || null,
      };
    },
    populateUserData(profile) {
      this.currentUser = {
        name: profile.fullName || (profile.appUser && profile.appUser.userName) || "",
        role: (profile.appUser && profile.appUser.role) || "",
        email: profile.email || "",
      };
    },
    getImageUrl(url) {
      if (!url) return "";
      const base = typeof apiUrl !== "undefined" ? apiUrl : "";
      return (base.endsWith("/") ? base.slice(0, -1) : base) + url;
    },

    // ===== Formatting & display =====
    formatCurrency(amount, currency) {
      const cur = currency || this.businessCurrency || "MYR";
      const val = Number(amount) || 0;
      if (cur === "MYR") return val < 0 ? `-RM ${formatMyr(-val)}` : `RM ${formatMyr(val)}`;
      try {
        return new Intl.NumberFormat("en-MY", { style: "currency", currency: cur }).format(val);
      } catch (e) {
        return `${cur} ${formatMyr(val)}`;
      }
    },
    formatPercent(n) {
      const v = this.round1(Math.abs(n));
      return `${v}%`;
    },
    trendClass(n) {
      return n >= 0 ? "text-success" : "text-danger";
    },
    formatDate(dateString) {
      return formatDisplayDate(dateString) || "—";
    },
    formatTimeAgo(date) {
      const days = diffDays(mytToday(), asDateString(date));
      if (days === 0) return "Today";
      if (days === 1) return "Yesterday";
      if (days > 1 && days < 7) return `${days} days ago`;
      return this.formatDate(date);
    },
    invoiceStatusColor(status) {
      switch (status) {
        case "Paid": return "success";
        case "Overdue": return "danger";
        case "Outstanding": return "warning";
        default: return "secondary";
      }
    },
    getDueDateClass(dateString) {
      if (!dateString) return "";
      // Overdue is the only state worth colouring as text; warning is never a text colour.
      return this.getDaysUntil(dateString) < 0 ? "text-danger" : "";
    },
    getDaysUntil(dateString) {
      return diffDays(asDateString(dateString), mytToday());
    },
    getDeadlineClass(dateString) {
      const d = this.getDaysUntil(dateString);
      if (d < 0) return "deadline-danger";
      if (d <= 3) return "deadline-warning";
      if (d <= 7) return "deadline-info";
      return "";
    },
    getDeadlineColor(dateString) {
      const d = this.getDaysUntil(dateString);
      if (d < 0) return "danger";
      if (d <= 3) return "warning";
      if (d <= 7) return "info";
      return "secondary";
    },
    getDeadlineDaysText(dateString) {
      const d = this.getDaysUntil(dateString);
      if (d < 0) return `${Math.abs(d)}d overdue`;
      if (d === 0) return "Due today";
      if (d === 1) return "Due tomorrow";
      return `In ${d} days`;
    },
    getCategoryColor(index) {
      const colors = [BRAND.navy, BRAND.accent, BRAND.danger, BRAND.warning, BRAND.info, BRAND.gray];
      return colors[index % colors.length];
    },

    // ===== Navigation =====
    navigateTo(url) {
      if (url && this.$route.path !== url) this.$router.push(url).catch(() => {});
    },
    editBusinessInfo() {
      if (this.currentProfile && this.currentProfile.defaultBusiness) {
        this.$router.push(`/tenants/Business/${this.currentProfile.defaultBusiness.id}`);
      }
    },
    toggleBusinessInfo() {
      this.showBusinessInfo = !this.showBusinessInfo;
    },
  },
};
</script>

<style scoped>
/* ===== Header ===== */
.dash-header {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.75rem;
}
.dash-title {
  font-size: 1.75rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--marketing-primary);
  margin: 0 0 0.25rem;
}
.dash-subtitle {
  color: var(--marketing-gray-500);
  margin: 0;
}
.dash-header-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.period-group .period-btn {
  border: 1px solid var(--marketing-gray-300);
  background: #fff;
  color: var(--marketing-gray-600);
  font-weight: 600;
  transition: all 0.15s ease;
}
.period-group .period-btn.active {
  background: var(--marketing-primary);
  border-color: var(--marketing-primary);
  color: #fff;
}
.refresh-btn {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid var(--marketing-gray-300);
  background: #fff;
  color: var(--marketing-gray-600);
  transition: all 0.15s ease;
}
.refresh-btn:hover:not(:disabled) {
  color: var(--marketing-accent);
  border-color: var(--marketing-accent);
}
.refresh-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.spin { animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* ===== Cards ===== */
.dash-card {
  border: 1px solid var(--marketing-gray-200);
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}
.dash-card-header {
  background: transparent;
  border-bottom: 1px solid var(--marketing-gray-100);
  padding: 1.1rem 1.5rem 0.9rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.dash-card-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--marketing-primary);
}
.text-brand-primary { color: var(--marketing-primary) !important; }
.text-brand-accent { color: var(--marketing-accent) !important; }

.btn-brand-accent {
  background: var(--marketing-accent);
  border-color: var(--marketing-accent);
  color: #fff;
  font-weight: 600;
  transition: transform 0.16s ease;
}
.btn-brand-accent:hover { background: var(--marketing-accent-dark); color: #fff; }
.btn-brand-accent:active { transform: scale(0.97); }
.btn-brand-outline {
  border: 1px solid var(--marketing-primary);
  color: var(--marketing-primary);
  background: #fff;
  font-weight: 600;
}
.btn-brand-outline:hover { background: var(--marketing-primary); color: #fff; }

/* ===== Metric callouts ===== */
.metric {
  border-radius: 12px;
  padding: 1rem 1.1rem;
  background: var(--marketing-gray-50);
  border: 1px solid var(--marketing-gray-200);
  height: 100%;
}
.metric-label {
  font-size: 0.74rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--marketing-gray-500);
  margin-bottom: 0.3rem;
}
.metric-value { font-size: 1.5rem; font-weight: 800; color: var(--marketing-gray-900); line-height: 1.1; }
.metric-sub { font-size: 0.78rem; margin-top: 0.3rem; color: var(--marketing-gray-500); }
/* Semantic colour via subtle tint + coloured label (no side-stripe). */
.metric-info { background: rgba(59, 130, 246, 0.07); }
.metric-info .metric-label { color: var(--marketing-info); }
.metric-success { background: rgba(16, 185, 129, 0.08); }
.metric-success .metric-label { color: var(--marketing-accent-dark); }
.metric-danger { background: rgba(239, 68, 68, 0.07); }
.metric-danger .metric-label { color: var(--marketing-danger); }
.metric-navy { background: rgba(30, 58, 95, 0.06); }
.metric-navy .metric-label { color: var(--marketing-primary); }

/* ===== Tables ===== */
.dash-table th {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--marketing-gray-500);
  border-top: none;
  border-bottom: 1px solid var(--marketing-gray-200);
}
.dash-table td { vertical-align: middle; border-top: 1px solid var(--marketing-gray-100); }
.cat-dot { display: inline-block; width: 10px; height: 10px; border-radius: 50%; margin-right: 8px; }

/* ===== Chart / empty placeholders ===== */
.chart-placeholder {
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.empty-state {
  text-align: center;
  padding: 2.5rem 1rem;
  color: var(--marketing-gray-500);
}

/* ===== Deadlines ===== */
.deadline-item {
  padding: 0.85rem 1rem;
  border-radius: 10px;
  background: var(--marketing-gray-50);
  margin-bottom: 0.65rem;
}
.deadline-item:last-child { margin-bottom: 0; }
/* Urgency shown via tint + the badge on the right (no side-stripe). */
.deadline-danger { background: rgba(239, 68, 68, 0.08); }
.deadline-warning { background: rgba(245, 158, 11, 0.09); }
.deadline-info { background: rgba(59, 130, 246, 0.07); }

/* ===== Timeline ===== */
.timeline { position: relative; padding-left: 0.5rem; }
.timeline-item { position: relative; display: flex; gap: 0.85rem; padding-bottom: 1.1rem; }
.timeline-item:not(:last-child)::before {
  content: "";
  position: absolute;
  left: 17px;
  top: 36px;
  bottom: 0;
  width: 1px;
  background: var(--marketing-gray-200);
}
.timeline-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  color: #fff;
  flex: 0 0 auto;
  z-index: 1;
}
.avatar-primary { background: var(--marketing-primary); }
.avatar-success { background: var(--marketing-accent); }
.avatar-danger { background: var(--marketing-danger); }
.avatar-warning { background: var(--marketing-warning); }
.timeline-content { flex: 1 1 auto; }

/* ===== Quick actions / shortcuts ===== */
.quick-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0.75rem; }
.quick-action {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.4rem;
  padding: 0.9rem 0.5rem;
  border-radius: 12px;
  border: 1px solid var(--marketing-gray-200);
  cursor: pointer;
  transition: all 0.16s ease;
}
.quick-icon {
  width: 40px; height: 40px; border-radius: 10px;
  display: grid; place-items: center; color: #fff;
}
.qi-primary { background: var(--marketing-primary); }
.qi-danger { background: var(--marketing-danger); }
.qi-warning { background: var(--marketing-warning); }
.qi-info { background: var(--marketing-info); }
.qi-success { background: var(--marketing-accent); }
.qi-navy { background: var(--marketing-primary-light); }
.quick-label { font-size: 0.78rem; font-weight: 600; color: var(--marketing-gray-700); }

.shortcut-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0.6rem; }
.shortcut {
  display: flex; flex-direction: column; align-items: center; gap: 0.35rem;
  padding: 0.7rem 0.4rem; border-radius: 10px; cursor: pointer; transition: all 0.16s ease;
}
.shortcut-icon { color: var(--marketing-primary); }
.shortcut-label { font-size: 0.7rem; color: var(--marketing-gray-600); text-align: center; }

.kpi-card:hover,
.quick-action:hover,
.shortcut:hover { background: var(--marketing-gray-50); }

/* ===== Business info (kept, retinted) ===== */
.cursor-pointer { cursor: pointer; }
.transition-icon { transition: transform 0.3s ease; }
.business-logo-large {
  width: 84px; height: 84px; border-radius: 12px; object-fit: cover;
  border: 1px solid var(--marketing-gray-200);
}
.business-logo-fallback {
  width: 84px; height: 84px; border-radius: 12px;
  display: grid; place-items: center;
  font-size: 2rem; font-weight: 800; color: #fff;
  background: var(--marketing-primary);
}
.info-section { background: var(--marketing-gray-50); padding: 1.5rem; border-radius: 10px; height: 100%; }
.section-title {
  color: var(--marketing-primary);
  font-weight: 700; font-size: 0.875rem;
  padding-bottom: 0.75rem; border-bottom: 2px solid var(--marketing-gray-200);
}
.info-item { padding-bottom: 0.75rem; border-bottom: 1px solid var(--marketing-gray-200); }
.info-item:last-child { border-bottom: none; padding-bottom: 0; }
.info-label {
  display: block; font-size: 0.8rem; font-weight: 600; color: var(--marketing-gray-500);
  margin-bottom: 0.4rem;
}
.info-value { font-size: 1.05rem; font-weight: 500; color: var(--marketing-gray-900); line-height: 1.4; }
.info-value-small { font-size: 0.85rem; margin-top: 0.25rem; }
.selectable-text { user-select: all; cursor: text; padding: 0.4rem; margin: -0.4rem; border-radius: 4px; transition: background 0.15s ease; }
.selectable-text:hover { background: var(--marketing-gray-100); }

/* ===== KPI cards ===== */
.kpi-row.loading { opacity: 0.6; pointer-events: none; }
.kpi-card {
  background: #fff;
  border: 1px solid var(--marketing-gray-200);
  border-radius: 14px;
  padding: 1.25rem 1.3rem;
  height: 100%;
}
.kpi-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 1rem; }
.kpi-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--marketing-gray-500);
}
.kpi-chip {
  width: 40px; height: 40px; border-radius: 11px;
  display: grid; place-items: center; font-size: 1.05rem;
}
.chip-navy { background: rgba(30, 58, 95, 0.10); color: var(--marketing-primary); }
.chip-success { background: rgba(16, 185, 129, 0.13); color: var(--marketing-accent-dark); }
.chip-danger { background: rgba(239, 68, 68, 0.11); color: var(--marketing-danger); }
.chip-warning { background: rgba(245, 158, 11, 0.14); color: #b45309; }
.kpi-value {
  font-size: 1.7rem; font-weight: 800; line-height: 1.05;
  letter-spacing: -0.02em; color: var(--marketing-primary);
}
.kpi-foot { display: flex; align-items: center; gap: 0.55rem; margin-top: 0.75rem; }
.kpi-trend {
  display: inline-flex; align-items: center; gap: 0.18rem;
  font-size: 0.76rem; font-weight: 700; padding: 0.14rem 0.5rem; border-radius: 20px;
}
.trend-up { color: var(--marketing-accent-dark); background: rgba(16, 185, 129, 0.12); }
.trend-down { color: var(--marketing-danger); background: rgba(239, 68, 68, 0.10); }
.trend-neutral { color: var(--marketing-gray-600); background: var(--marketing-gray-100); }
.kpi-sub { font-size: 0.74rem; color: var(--marketing-gray-400); }

/* ===== Responsive ===== */
@media (max-width: 767.98px) {
  .quick-grid { grid-template-columns: repeat(2, 1fr); }
  .shortcut-grid { grid-template-columns: repeat(4, 1fr); }
  .metric { margin-bottom: 1rem; }
  .dash-title { font-size: 1.4rem; }
}

@media (prefers-reduced-motion: reduce) {
  .btn-brand-accent, .period-btn, .refresh-btn { transition: none; }
  .spin { animation: none; }
}
</style>
